/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { NotificationViewStyle } from '@airasia-phoenix/react-native/components/NotificationView';
import { SelectionFieldRefs } from '@airasia-phoenix/react-native/components/SelectionFieldView';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSsoErrorReporter } from './useSsoErrorReporter';
import { SCREEN_NAME } from '../config/analytics';
import { useTranslation } from 'react-i18next';
import { MobileLoginResponse } from '../managers';
import { describeRestoreError } from '../utils/accountRecovery';
import countryList from '../utils/countryList.json';
import { extractErrorMessage } from '../utils/errors';
import { CountryData, filterAndMapCountries } from '../utils/mapCountries';
import { withPlusDialingCode } from '../utils/phoneUtils';
import { mobileNumberError } from '../utils/signupLoginUtils';
import {
  LoginError,
  LoginNotification,
  MobileLoginErrorsType,
  MobileLoginType,
} from '../widgets/LoginView/types';
import { useLoginByMobile } from './account';
import type { KeepAccountRecoveryHandlers } from './useKeepAccountRecovery';

const DEFAULT_DIALING_CODE = '+60';

export interface UseMobileLoginFlowOptions {
  onMobileLoginSuccess?: (data: MobileLoginResponse) => void;
  onMobileLoginPress?: (data: MobileLoginType) => void;
  onLoginError?: (errorData: LoginError) => void;
  setNotification: (notification: LoginNotification | null) => void;
  accountRecovery?: KeepAccountRecoveryHandlers;
}

/** Mobile number + dialing code field state, validation and login submission. */
export function useMobileLoginFlow(options: UseMobileLoginFlowOptions) {
  const {
    onMobileLoginSuccess,
    onMobileLoginPress,
    onLoginError,
    setNotification,
    accountRecovery,
  } = options;

  const { t } = useTranslation('account');

  const { isPending: isMobileLoginPending, mutate: mobileLoginMutate } =
    useLoginByMobile();
  const reportError = useSsoErrorReporter(SCREEN_NAME.loginV2);

  const [mobileLogin, setMobileLogin] = useState<MobileLoginType>({
    mobileNumber: '',
    dialingCode: DEFAULT_DIALING_CODE,
  });
  const [countrySearch, setCountrySearch] = useState('');

  const [shouldShowMobileNumberError, setShouldShowMobileNumberError] =
    useState(false);

  const mobileFieldRef = useRef<SelectionFieldRefs>(null);

  const mobileNumberValidationError = mobileNumberError(
    mobileLogin.mobileNumber,
    t,
  );

  const mobileErrorMessage = shouldShowMobileNumberError
    ? mobileNumberValidationError
    : '';

  const mobileLoginErrors: MobileLoginErrorsType = {
    mobileNumberError: mobileErrorMessage !== '',
  };

  const handleMobileTextChange = useCallback(
    (text: string) => {
      setMobileLogin((prev) => ({ ...prev, mobileNumber: text }));
      setShouldShowMobileNumberError(true);
      setNotification(null);
    },
    [setNotification],
  );

  const handleDialingCodeChange = useCallback(
    (selectedCountryCode: string) => {
      setMobileLogin((prev) => ({
        ...prev,
        dialingCode: withPlusDialingCode(selectedCountryCode),
      }));
      setNotification(null);
    },
    [setNotification],
  );

  const validate = useCallback(() => {
    setShouldShowMobileNumberError(true);

    return mobileNumberValidationError === '';
  }, [mobileNumberValidationError]);

  const handleMobileLoginPress = useCallback(() => {
    if (!validate()) return;

    const payload = {
      mobileNumber: mobileLogin.mobileNumber.trim(),
      dialingCode: mobileLogin.dialingCode.replace('+', ''),
    };

    const onSuccess = (data: MobileLoginResponse) => {
      setShouldShowMobileNumberError(false);
      setNotification(null);
      onMobileLoginSuccess?.(data);
    };

    mobileLoginMutate(payload, {
      onSuccess,
      onError: (error) => {
        onLoginError?.({ method: 'mobile', error });

        if (
          accountRecovery?.tryHandleScheduledDeletion(error, () => {
            mobileLoginMutate(
              { ...payload, restoreAccount: true },
              {
                onSuccess,
                onError: (retryError) => {
                  const message = describeRestoreError(retryError, t);
                  reportError(retryError, message);
                  setNotification({
                    message,
                    type: NotificationViewStyle.ERROR,
                  });
                },
              },
            );
          })
        ) {
          setNotification(null);
          return;
        }

        const message = extractErrorMessage(error, t('loginPage.loginFailed'));
        reportError(error, message);
        setNotification({ message, type: NotificationViewStyle.ERROR });
      },
    });

    onMobileLoginPress?.({
      mobileNumber: mobileLogin.mobileNumber.trim(),
      dialingCode: mobileLogin.dialingCode,
    });
  }, [
    validate,
    mobileLogin,
    mobileLoginMutate,
    onMobileLoginSuccess,
    onMobileLoginPress,
    onLoginError,
    reportError,
    setNotification,
    accountRecovery,
    t,
  ]);

  const filteredCountryData = useMemo(
    () => [
      {
        title: 'Popular',
        data: filterAndMapCountries(
          countryList.popularCountries as CountryData[],
          countrySearch,
        ),
      },
      {
        title: 'All',
        data: filterAndMapCountries(
          countryList.countries as CountryData[],
          countrySearch,
        ),
      },
    ],
    [countrySearch],
  );

  return {
    mobileLogin,
    mobileLoginErrors,
    mobileErrorMessage,
    mobileFieldRef,
    countrySearch,
    setCountrySearch,
    filteredCountryData,
    handleMobileTextChange,
    handleDialingCodeChange,
    handleMobileLoginPress,
    isMobileLoginPending,
  };
}
